import { MoreVertRounded } from "@mui/icons-material";
import { Box, Chip, IconButton, Link, Menu, MenuItem, Stack, styled, Typography } from "@mui/material";
import { useState } from "react";
import { EditMetricModal, Chart, LinkBehavior } from "src/components";
import { Links } from "src/constants";
import { MetricDto } from "src/http";
import { MetricMapper } from "src/mapping";
import { useConfirm } from "material-ui-confirm";

const MetricLink = styled(Link)`
  color: ${({ theme }) => theme.palette.text.primary};
  display: block;

  &:hover {
    text-decoration: none;
  }
`;

interface Props {
  metric: MetricDto;
  userCanEdit: boolean;
  disabled: boolean;
  onRemoveMetricClicked: (metricGuid: string) => void;
}

const MetricPropertyMetric = (props: Props) => {
  const [menuAnchor, setMenuAchor] = useState<HTMLElement | null>(null);
  const [isEditModalVisible, setIsEditModalVisible] = useState(false);
  const confirm = useConfirm();

  async function handleRemoveClicked() {
    setMenuAchor(null);

    const { confirmed } = await confirm({
      title: "Remove",
      content: "Are you sure you want to remove this metric?",
      confirmationText: "Remove",
      cancellationText: "Cancel",
    });

    if (!confirmed) {
      return;
    }

    props.onRemoveMetricClicked(props.metric.guid);
  }

  return (
    <>
      <Stack direction="row" sx={{ alignItems: "center", justifyContent: "space-between", width: "100%" }}>
        <Stack spacing={0.5} sx={{ flexShrink: 1, flexGrow: 1, overflow: "hidden" }}>
          <Stack direction="row" spacing={0.5}>
            {props.metric.assignedTeam && (
              <Chip
                label={props.metric.assignedTeam.name}
                color="primary"
                component={LinkBehavior}
                href={Links.Team(props.metric.assignedTeam.slug)}
                clickable
              />
            )}

            {props.metric.reportingUser && (
              <Chip
                label={props.metric.reportingUser.displayName}
                color="person"
                component={LinkBehavior}
                href={Links.User(props.metric.reportingUser.userId)}
                clickable
              />
            )}
          </Stack>

          <MetricLink href={Links.MetricDetail(props.metric.guid)}>
            <Box>
              <Typography variant="body2">{props.metric.title}</Typography>
              <Typography variant="caption">{props.metric.description}</Typography>
            </Box>

            <Chart
              datasets={MetricMapper.toChartDataWithNulls(props.metric).map((data) => {
                return {
                  title: data.title,
                  displayAsBar: data.displayAsBar,
                  color: data.color,
                  dotted: data.dotted,
                  data: data.chartData,
                  sortOrder: data.sortOrder,
                };
              })}
              annotations={props.metric.annotations}
            />
          </MetricLink>
        </Stack>

        {props.userCanEdit && !props.disabled && (
          <IconButton
            size="small"
            onClick={(e) => {
              e.preventDefault();
              setMenuAchor(e.currentTarget);
            }}
            sx={{ flexShrink: 0, flexGrow: 0 }}
          >
            <MoreVertRounded />
          </IconButton>
        )}
      </Stack>

      <Menu anchorEl={menuAnchor} open={Boolean(menuAnchor)} onClose={() => setMenuAchor(null)}>
        {props.metric.userCanEdit && (
          <MenuItem
            onClick={() => {
              setIsEditModalVisible(true);
              setMenuAchor(null);
            }}
          >
            Update
          </MenuItem>
        )}
        {props.userCanEdit && <MenuItem onClick={handleRemoveClicked}>Remove</MenuItem>}
      </Menu>

      <EditMetricModal
        isVisible={isEditModalVisible}
        metric={props.metric}
        onUpdated={() => setIsEditModalVisible(false)}
        onCancelled={() => setIsEditModalVisible(false)}
      />
    </>
  );
};

export { MetricPropertyMetric };
